/**
 * Modal Controller
 *
 * Handles modal open/close, section switching, and keyboard navigation.
 */

export default class Modal {
    constructor() {
        this.modal = null;
        this.overlay = null;
        this.currentSection = 'login';
        this.isOpen = false;

        this.init();
    }

    /**
     * Initialize the modal controller.
     */
    init() {
        this.modal = document.querySelector('.wd-lp-modal');
        this.overlay = document.querySelector('.wd-lp-overlay');

        // Debug logging
        console.log('[WD Login Popup] Init:', {
            modalFound: !!this.modal,
            overlayFound: !!this.overlay,
            triggers: document.querySelectorAll('.wd-lp-trigger, .wlrp-popup-open').length
        });

        if (!this.modal) {
            console.warn('[WD Login Popup] Modal element not found in DOM. Make sure the shortcode or footer popup is rendered.');
            return;
        }

        this.bindTriggers();
        this.bindNavigation();
        this.bindClose();
        this.bindKeyboard();
        this.bindSectionSwitchers();
    }

    /**
     * Bind click triggers for opening the modal.
     */
    bindTriggers() {
        // Support both new class and legacy wlrp-popup-open
        const triggers = document.querySelectorAll('.wd-lp-trigger, .wlrp-popup-open');

        triggers.forEach(trigger => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();

                const href = trigger.getAttribute('href') || '';
                let section = 'login';

                // Parse href for specific section
                if (href.includes('register') || href === '#wlrp-popup-register') {
                    section = 'register';
                } else if (href.includes('password') || href === '#wlrp-popup-password') {
                    section = 'password';
                }

                this.open(section);
            });
        });
    }

    /**
     * Bind tab navigation clicks.
     */
    bindNavigation() {
        const tabs = this.modal?.querySelectorAll('.wd-lp-tab, [data-target]');

        tabs?.forEach(tab => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                const target = tab.dataset.target;
                if (target) {
                    this.showSection(target);
                }
            });
        });
    }

    /**
     * Bind section switcher links.
     */
    bindSectionSwitchers() {
        const switchers = this.modal?.querySelectorAll('.wd-lp-link[data-target], .wlrp-popup-switch');

        switchers?.forEach(switcher => {
            switcher.addEventListener('click', (e) => {
                e.preventDefault();
                const target = switcher.dataset.target;
                if (target) {
                    this.showSection(target);
                }
            });
        });
    }

    /**
     * Bind close button and overlay clicks.
     */
    bindClose() {
        // Close button
        const closeBtn = this.modal?.querySelector('.wd-lp-close, .wlrp-popup-close');
        closeBtn?.addEventListener('click', () => this.close());

        // Overlay click
        this.overlay?.addEventListener('click', () => this.close());
    }

    /**
     * Bind keyboard events.
     */
    bindKeyboard() {
        document.addEventListener('keydown', (e) => {
            if (!this.isOpen) return;

            // Escape key to close
            if (e.key === 'Escape') {
                this.close();
            }

            // Tab key for focus trap
            if (e.key === 'Tab') {
                this.handleTabKey(e);
            }
        });
    }

    /**
     * Handle tab key for focus trapping.
     */
    handleTabKey(e) {
        const focusableElements = this.modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        } else {
            // Tab
            if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        }
    }

    /**
     * Open the modal.
     *
     * @param {string} section - Section to show (login, register, password)
     */
    open(section = 'login') {
        console.log('[WD Login Popup] Opening modal:', { section, modalExists: !!this.modal });

        if (!this.modal) {
            console.error('[WD Login Popup] Cannot open - modal not found');
            return;
        }

        this.showSection(section);
        this.modal.classList.remove('wd-lp-modal--hidden'); // Remove hidden state
        this.modal.classList.add('wd-lp-modal--open');
        this.modal.classList.add('wlrp-popup-modal-on'); // Legacy class
        this.overlay?.classList.add('wd-lp-overlay--visible');
        document.body.classList.add('wd-lp-body-locked');
        this.isOpen = true;

        console.log('[WD Login Popup] Modal classes after open:', this.modal.className);

        // Focus first input after animation
        setTimeout(() => {
            const activeSection = this.modal.querySelector(`[data-section="${section}"]`);
            const firstInput = activeSection?.querySelector('input:not([type="hidden"])');
            firstInput?.focus();
        }, 300);

        // Dispatch custom event
        this.modal.dispatchEvent(new CustomEvent('wdLoginPopupOpen', {
            detail: { section }
        }));
    }

    /**
     * Close the modal.
     */
    close() {
        if (!this.modal) return;

        this.modal.classList.remove('wd-lp-modal--open');
        this.modal.classList.remove('wlrp-popup-modal-on');
        this.modal.classList.add('wd-lp-modal--hidden'); // Restore hidden state
        this.overlay?.classList.remove('wd-lp-overlay--visible');
        document.body.classList.remove('wd-lp-body-locked');
        this.isOpen = false;

        // Clear any messages
        const messages = this.modal.querySelectorAll('.wd-lp-messages');
        messages.forEach(container => {
            container.innerHTML = '';
        });

        // Reset reCAPTCHA if present
        if (typeof grecaptcha !== 'undefined') {
            try {
                grecaptcha.reset();
            } catch (e) {
                // Ignore if no reCAPTCHA widget
            }
        }

        // Dispatch custom event
        this.modal.dispatchEvent(new CustomEvent('wdLoginPopupClose'));
    }

    /**
     * Show a specific section.
     *
     * @param {string} section - Section to show (login, register, password)
     */
    showSection(section) {
        if (!this.modal) return;

        this.currentSection = section;

        // Update sections visibility
        const sections = this.modal.querySelectorAll('[data-section]');
        sections.forEach(el => {
            const isTarget = el.dataset.section === section;
            el.classList.toggle('wd-lp-section--active', isTarget);
            el.hidden = !isTarget;
        });

        // Update tab active states
        const tabs = this.modal.querySelectorAll('.wd-lp-tab, [data-target]');
        tabs.forEach(tab => {
            const isActive = tab.dataset.target === section;
            tab.classList.toggle('wd-lp-tab--active', isActive);
            tab.classList.toggle('wlrp-active', isActive); // Legacy class
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        // Update title for non-tabbed layouts
        const titleTexts = this.modal.querySelectorAll('.wd-lp-title-text');
        titleTexts.forEach(text => {
            text.style.display = text.dataset.section === section ? '' : 'none';
        });

        // Update modal data attribute for CSS hooks
        this.modal.dataset.visible = section;

        // Dispatch custom event
        this.modal.dispatchEvent(new CustomEvent('wdLoginPopupSectionChange', {
            detail: { section }
        }));
    }

    /**
     * Get the current section.
     *
     * @returns {string}
     */
    getCurrentSection() {
        return this.currentSection;
    }

    /**
     * Check if modal is currently open.
     *
     * @returns {boolean}
     */
    getIsOpen() {
        return this.isOpen;
    }
}
